import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import SVG from 'components/base/SVG';
import Tooltip from '../../app/components/Tooltip';

import css from './style.scss';


const OutlinedButton = ({ as: Component, icon, leftIcon, rightIcon, text, children, loading, disabled, orangeTheme, solidPrimary, active, onlyIcon, className, type, id, onClick, onRef, url, target, download, ...rest }) => {
  const isButton = Component === 'button';
  const isDisabled = disabled || loading;
  // `icon` is a backward-compatible alias for the left icon.
  const left = leftIcon || icon;
  // In `onlyIcon` mode the label is hidden and surfaced as a Tooltip instead.
  const showText = text && !onlyIcon;

  // Handle URL logic
  const external = url && url.startsWith('http');
  const Anchor = external ? 'a' : Link;
  const anchorProps = external && download ? { download } : {};

  const baseProps = {
    ref: onRef,
    onClick,
    className: classNames(css.outlined, { [css.loading]: loading, [css.disabled]: isDisabled, [css.orangeTheme]: orangeTheme, [css.solidPrimary]: solidPrimary, [css.active]: active, [css.onlyIcon]: onlyIcon }, className),
    ...rest,
  };

  if (id) baseProps.id = id;

  // Determine the final component to render
  let FinalComponent = Component;
  let finalProps = { ...baseProps };

  if (url) {
    // If URL is provided, use Anchor (a or Link) as the component
    FinalComponent = Anchor;
    finalProps = {
      ...baseProps,
      [external ? 'href' : 'to']: url,
      target: target,
      ...anchorProps
    };
    // Remove 'type' and 'disabled' for anchor elements
    delete finalProps.type;
    delete finalProps.disabled;
    // Add aria-disabled if disabled
    if (isDisabled) {
      finalProps['aria-disabled'] = true;
    }
  } else if (isButton) {
    // Button specific props
    finalProps.type = type;
    finalProps.disabled = isDisabled;
  } else if (isDisabled) {
    // Non-button components
    finalProps['aria-disabled'] = true;
  }

  const button = (
    <FinalComponent {...finalProps}>
      <div className={css.content}>
        {children}
        {left && <SVG icon={left} className={css.outlinedIcon} />}
        {!showText ? null : <div className={css.text}>{text}</div>}
        {rightIcon && <SVG icon={rightIcon} className={classNames(css.outlinedIcon, css.outlinedIconRight)} />}
      </div>
      <div className={css.spinner}>
        <SVG icon="spinner" />
      </div>
    </FinalComponent>
  );

  // In `onlyIcon` mode the hidden label is surfaced as a Tooltip on hover.
  if (onlyIcon && text) {
    return (
      <Tooltip content={text} position="bottom" arrowOffset="50%" yOffset={-10}>
        {button}
      </Tooltip>
    );
  }

  return button;
};

OutlinedButton.propTypes = {
  as: PropTypes.elementType,
  // `icon` is a backward-compatible alias for `leftIcon`.
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  leftIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  rightIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  text: PropTypes.string,
  children: PropTypes.node,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  orangeTheme: PropTypes.bool,
  solidPrimary: PropTypes.bool,
  // When true, keep the hover/pressed styling applied — e.g. while a dropdown this button triggers is open.
  active: PropTypes.bool,
  // When true, render only the icon and expose `text` as a native tooltip.
  onlyIcon: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  id: PropTypes.string,
  onClick: PropTypes.func,
  onRef: PropTypes.func,
  // New URL-related props
  url: PropTypes.string,
  target: PropTypes.string,
  download: PropTypes.bool,
};

OutlinedButton.defaultProps = {
  as: 'button',
  loading: false,
  disabled: false,
  orangeTheme: false,
  solidPrimary: false,
  active: false,
  onlyIcon: false,
  type: 'button',
  download: false,
};

export default OutlinedButton;
